import type { AxiosError } from 'axios'
import { groupBy } from 'lodash'
import type { ZodCustomIssue } from 'zod'
import type { ValidationError } from '../components/validation-error-popover'
import type { GenericNode } from '../types'

/**
 * Maps backend node ID to frontend node using payload matching
 */
export function mapBackendNodeToFrontend(
  backendNodeId: string,
  frontendNodes: GenericNode[],
  nodeIdMapping?: Record<string, string> | null,
): GenericNode | undefined {
  // Try exact ID match first (works for drafts)
  let matchingNode = frontendNodes.find((n) => n.id === backendNodeId)

  // Use direct mapping if available (works for publish)
  if (!matchingNode && nodeIdMapping) {
    const originalFrontendId = Object.keys(nodeIdMapping).find(
      (key) => nodeIdMapping[key] === backendNodeId,
    )

    if (originalFrontendId) {
      matchingNode = frontendNodes.find((n) => n.id === originalFrontendId)
    }
  }

  return matchingNode
}

/**
 * Converts technical field paths to user-friendly display names
 */
function getFieldDisplayName(field: string): string {
  // Remove array indices and technical nesting - extract the meaningful field name
  const cleanField =
    field
      .replace(/\[\d+\]/g, '') // Remove [0], [1], etc.
      .replace(/^\d+\./, '') // Remove leading numbers like "0."
      .split('.') // Split by dots
      .pop() || field // Take the last part (actual field name)

  // Convert snake_case and kebab-case to space-separated words
  const displayName = cleanField
    .replace(/[_-]/g, ' ') // Replace underscores and hyphens with spaces
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Handle camelCase
    .toLowerCase()

  // Capitalize first letter of each word
  return displayName
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Formats error messages with field context
 */
function formatErrorMessage(field: string, message: string): string {
  const fieldDisplayName = getFieldDisplayName(field)

  // If message already mentions the field, return as-is
  if (message.toLowerCase().includes(fieldDisplayName.toLowerCase())) {
    return message
  }

  // Add field context to the message
  return `${fieldDisplayName}: ${message}`
}

/**
 * Recursively extracts error messages from nested validation structures
 */
function extractErrorMessages(
  obj: unknown,
  fieldPath = '',
): Array<{ field: string; message: string }> {
  const results: Array<{ field: string; message: string }> = []

  if (typeof obj === 'string') {
    results.push({ field: fieldPath, message: obj })
    return results
  }

  if (Array.isArray(obj)) {
    obj.forEach((item, index) => {
      const nestedPath = fieldPath ? `${fieldPath}[${index}]` : `${index}`
      results.push(...extractErrorMessages(item, nestedPath))
    })
    return results
  }

  if (obj && typeof obj === 'object') {
    Object.entries(obj).forEach(([key, value]) => {
      const nestedPath = fieldPath ? `${fieldPath}.${key}` : key

      // If the value is an array of strings, treat them as error messages
      if (Array.isArray(value) && value.every((item) => typeof item === 'string')) {
        value.forEach((message) => {
          results.push({ field: nestedPath, message })
        })
      } else {
        // Continue recursing
        results.push(...extractErrorMessages(value, nestedPath))
      }
    })
  }

  return results
}

/**
 * Shared utility to process node issues from backend validation response
 * Handles both direct error strings and deeply nested structured config errors
 */
export function processNodeIssues<T>(
  nodeIssues: unknown,
  nodeId: string,
  processor: {
    handleDirectError: (nodeId: string, message: string) => T
    handleConfigError: (nodeId: string, field: string, message: string) => T
  },
): T[] {
  if (!Array.isArray(nodeIssues)) return []

  const results: T[] = []

  nodeIssues.forEach((issue: Record<string, unknown> | string) => {
    // Handle direct error strings
    if (typeof issue === 'string') {
      results.push(processor.handleDirectError(nodeId, issue))
      return
    }

    // Handle structured config errors with deep nesting
    if (issue.config && typeof issue.config === 'object') {
      const errorMessages = extractErrorMessages(issue.config)
      errorMessages.forEach(({ field, message }) => {
        results.push(processor.handleConfigError(nodeId, field, message))
      })
    }
  })

  return results
}

/**
 * Processes backend validation errors and converts them to structured ValidationError format
 */
export function processBackendValidationErrors(
  error: unknown,
  nodes?: GenericNode[],
  nodeIdMapping?: Record<string, string> | null,
): ValidationError[] {
  const validationErrors: ValidationError[] = []

  if (!error || typeof error !== 'object' || !('response' in error)) {
    return validationErrors
  }

  const axiosError = error as AxiosError
  const responseData = axiosError.response?.data?.data

  // Check for PHLO-level error message
  if (responseData?.message && typeof responseData.message === 'string') {
    validationErrors.push({
      stepTitle: 'Agent flow level errors',
      errors: [responseData.message],
      // No nodeId for PHLO-level errors
    })
  }

  // Global Prompt errors — keyed by `global_system_prompt` because the
  // system_prompt node is a UI-only virtual node and lives outside `nodes`.
  if (Array.isArray(responseData?.global_system_prompt)) {
    const globalPromptErrors = responseData.global_system_prompt.filter(
      (m: unknown): m is string => typeof m === 'string',
    )
    if (globalPromptErrors.length > 0) {
      validationErrors.push({
        stepTitle: 'Global Prompt',
        errors: globalPromptErrors,
      })
    }
  }

  // Check if the response contains validation errors in the expected format
  if (!responseData?.nodes || typeof responseData.nodes !== 'object') {
    return validationErrors
  }

  // Create a map of node ID to node name for quick lookup
  const nodeIdToName = new Map<string, string>()
  if (nodes) {
    nodes.forEach((node) => {
      // Safely access node name with fallback
      const nodeName = node.data?.config?.name || `Step`
      nodeIdToName.set(node.id, nodeName)
    })
  }

  // Process each node error using the node ID directly
  Object.entries(responseData.nodes).forEach(([nodeId, nodeIssues]) => {
    const errors = processNodeIssues(nodeIssues, nodeId, {
      handleDirectError: (_, message) => message,
      handleConfigError: (_, field, message) => formatErrorMessage(field, message),
    })

    if (errors.length > 0) {
      // Get the actual node name if available, otherwise use generic title
      let stepTitle = nodeIdToName.get(nodeId)

      // If we can't find the node name by exact ID, try to match using mapping or the sent payload
      if (!stepTitle) {
        const matchingNode = nodes
          ? mapBackendNodeToFrontend(nodeId, nodes, nodeIdMapping)
          : undefined

        if (matchingNode) {
          stepTitle =
            matchingNode.data?.config?.name ||
            `${matchingNode.type?.replace(/_/g, ' ') || 'Step'}`.replace(/\b\w/g, (l) =>
              l.toUpperCase(),
            )
        } else {
          stepTitle = 'Step'
        }
      }

      validationErrors.push({
        stepTitle,
        errors,
        nodeId,
      })
    }
  })

  return validationErrors
}

/**
 * Processes frontend node validation issues and converts them to ValidationError format
 */
export function processNodeValidationIssues(
  nodeIssues: ZodCustomIssue[],
  nodes: GenericNode[],
): ValidationError[] {
  // Group issues by node ID using lodash, filtering out issues without nodeId
  const issuesByNode = groupBy(
    nodeIssues.filter((issue) => issue.params?.data?.id),
    (issue) => issue.params?.data?.id,
  )

  // Convert to ValidationError format with functional approach for step numbering
  return Object.entries(issuesByNode)
    .map(([nodeId, issues], index) => {
      const node = nodes.find((n) => n.id === nodeId)
      if (!node) return null

      const errors = (issues as ZodCustomIssue[])
        .map((issue) => issue.message)
        .filter((message): message is string => message !== undefined)

      if (errors.length === 0) return null

      return {
        stepNumber: index + 1,
        stepTitle: node.data.config.name,
        errors,
        nodeId,
      }
    })
    .filter((error) => error !== null) as ValidationError[]
}

/**
 * Main function to process any type of validation errors
 */
export function processValidationErrors(
  error: unknown,
  nodes?: GenericNode[],
  frontendIssues?: ZodCustomIssue[],
  nodeIdMapping?: Record<string, string> | null,
): ValidationError[] {
  const allErrors: ValidationError[] = []

  // Process backend errors if available
  if (error) {
    const backendErrors = processBackendValidationErrors(error, nodes, nodeIdMapping)
    allErrors.push(...backendErrors)
  }

  // Process frontend errors if available
  if (frontendIssues && nodes) {
    const frontendErrors = processNodeValidationIssues(frontendIssues, nodes)
    allErrors.push(...frontendErrors)
  }

  // Since we're using simple sequential numbering (1, 2, 3...),
  // just return errors in the order they were processed
  // Remove duplicates within each error's messages by creating new objects
  return allErrors.map((validationError) => ({
    ...validationError,
    errors: [...new Set(validationError.errors)],
  }))
}
